import { ModalBase } from '@/components/molecules'
import { config } from '@/config'
import {
  AssessmentStepKeys,
  Breakpoint,
  ComponentColors,
  ThemeColor,
} from '@/enums'
import { LeftArrow } from '@/icons'
import { useABTest } from '@/modules/ABTest/ABTestContext'
import { ONGOING_TESTS } from '@/modules/ABTest/constants/ongoingTests'
import { useSegment } from '@/modules/analytics'
import { StepperTestimonial } from '@/pages/SelfAssessmentPage/steps/StepperTestimonial'
import { ContactDataImage } from '@/pages/SelfAssessmentPage/steps/components/ContactDataStepA'
import { StepperProvider, useStepperStore } from '@/providers'
import { theme } from '@/theme'
import {
  checkIsInPartnerUrl,
  extractFunnelFromPathname,
  goToExternalUrl,
  pushEventToDataLayer,
  returns,
  useChangeSearchParam,
  useIsMobile,
  useModal,
  useSteps,
} from '@/utils'
import { Button } from '@surein/ui'
import { ArrowRightIcon, ChevronLeftIcon } from 'lucide-react'
import { isNil, pluck, uniq } from 'ramda'
import { ElementType, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageFromCdn, LineProgress, Typography } from '../../atoms'
import { ShowWhen, WithWrapper } from '../../hoc'
import { Column, Row } from '../../styled'
import { Breadcrumbs } from './Breadcrumbs'
import {
  BackContainer,
  ButtonContainer,
  Container,
  ContinueButton,
  FunnelTestimonialContainer,
  SkipModalSubheader,
  StepContainer,
  StepViewport,
  StepperContainer,
  StepperHeader,
} from './styles'

export interface StepData {
  component: ElementType
  img?: ElementType
  key: string
  stepIndex: number
}

interface StepProps {
  img?: ElementType
  hideButtons?: boolean
  [key: string]: any
}

export interface StepperComponentProps {
  steps: StepData[]
  initialStep?: string
  onCancel?: () => void
  onFinish?: () => void
  withButtons?: boolean
  hideLastStepButton?: boolean
  topRight?: ElementType
  stepWrapper: ElementType
  shouldUpdateUrl?: boolean
  stepProps?: Record<string, StepProps>
  stepperBreadcrumbTitles?: Record<string, string>
  onLogoClick?: () => void
}

interface BackButtonProps {
  onBack?: () => void
}

interface Messages {
  back: string
  next: string
  skipForNow: string
  confirmSkip: string
  rejectSkip: string
  contactDataImageText: Record<string, string>
  funnelTestimonial: Record<string, string>
}

const useMessages = (): Messages => {
  const { t } = useTranslation()

  return {
    back: t('common.back'),
    next: t('continue'),
    skipForNow: t('skipForNow'),
    confirmSkip: t('confirmSkip'),
    rejectSkip: t('rejectSkip'),
    contactDataImageText: t('contactDataImageText'),
    funnelTestimonial: t('funnelTestimonial'),
  }
}

export const BackButton = ({ onBack }: BackButtonProps) => {
  const messages = useMessages()

  return (
    <BackContainer onClick={onBack}>
      <Typography color={ThemeColor.b50} singleLine variant="captionR">
        <LeftArrow stroke={ThemeColor.b50} />
        <span>{messages.back}</span>
      </Typography>
    </BackContainer>
  )
}

const defaultLogoClick = () => goToExternalUrl(config.landingPage)

const Component = ({
  steps,
  initialStep,
  onCancel,
  onFinish,
  withButtons = true,
  hideLastStepButton = false,
  topRight: TopRight = returns(<div />),
  stepWrapper,
  shouldUpdateUrl,
  stepProps = {},
  stepperBreadcrumbTitles,
  onLogoClick = defaultLogoClick,
}: StepperComponentProps) => {
  const { isMobile, width } = useIsMobile(Breakpoint.md)
  // TODO: We will need to refactor this to use useModifySearchParam, this hook updates the URL
  // but it all happens outside of the context of the router, which causes issues with the router
  // and the history of the app
  const changeStepParam = useChangeSearchParam()
  const {
    nextDisabled,
    nextLoading,
    onNextClick,
    onBackClick,
    skipData,
    buttonText,
  } = useStepperStore()
  const messages = useMessages()
  const { step, isLast, isFirst, index, data, back, next, goTo } = useSteps({
    initialStep,
    steps,
    keyPropName: 'key',
  })

  const { isOpen, open, close } = useModal()

  const hasBreadcrumbs = !!stepperBreadcrumbTitles

  const analytics = useSegment()

  const { variant } = useABTest(ONGOING_TESTS.businessTypeSelectorSort2410)

  useEffect(() => {
    const stepContainer = document.getElementById(
      'step-container',
    ) as HTMLElement
    stepContainer?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })

    if (shouldUpdateUrl) {
      changeStepParam({ step })
      // We check if the variant is not null or undefined to avoid sending the event
      // twice on start, as the variant is set on the first render
      if (!isNil(variant)) {
        pushEventToDataLayer('Step Change', {
          funnel_product: extractFunnelFromPathname(),
          funnel_step: step,
        })
        analytics.page({
          name: 'Page View',
          page_name: step,
          [ONGOING_TESTS.businessTypeSelectorSort2410]: variant,
        })
      }
    }
  }, [step, variant])

  const handleBack = () => {
    if (onBackClick) {
      onBackClick()
    } else if (!isFirst) back()
    else onCancel?.()
  }

  const handleNext = () => {
    if (!isLast) {
      next()
    } else {
      onFinish?.()
    }
  }

  const handleSkip = () => {
    skipData.onSkipClick()
    handleNext()
  }

  const handleSkipClick = () => {
    if (skipData.withModal) {
      open()
    } else handleSkip()
  }

  const onPrimaryClick = async () => {
    try {
      await onNextClick?.()
      handleNext()
    } catch (e) {
      return e
    }
    return undefined
  }

  const typedData = (data as unknown) as StepData
  const { component: Step, img } = typedData
  const ImageSrc = stepProps[step]?.img || img

  const stepIndexes = uniq(pluck('stepIndex', steps))
  const breadcrumbSteps = [...stepIndexes, Math.max(...stepIndexes) + 1]

  return (
    <Container>
      <StepperContainer>
        <StepViewport>
          <StepperHeader hasBreadcrumbs={hasBreadcrumbs}>
            <ImageFromCdn
              height={20}
              name="SureinLogo2024"
              onClick={checkIsInPartnerUrl() ? undefined : onLogoClick}
              type="svg"
              width={74}
            />
            {hasBreadcrumbs ? (
              <Breadcrumbs
                current={typedData.stepIndex}
                stepperBreadcrumbTitles={stepperBreadcrumbTitles || {}}
                steps={breadcrumbSteps}
              />
            ) : (
              <div />
            )}
            <Row gap="14px">
              <TopRight />
            </Row>
          </StepperHeader>
          <StepContainer id="step-container" withButtons={withButtons}>
            <Column gap="24px">
              {!hasBreadcrumbs && (
                <LineProgress
                  color={ComponentColors.primary}
                  height="8px"
                  percentage={((index + 1) / steps.length) * 100}
                  width="176px"
                />
              )}
              {width < theme.breakpoints.values[Breakpoint.md] &&
                step === AssessmentStepKeys.contactData && <ContactDataImage />}
              <WithWrapper
                step={step}
                wrapper={stepWrapper}
                wrapWhen={stepWrapper}
              >
                <Step
                  goTo={goTo}
                  onBack={handleBack}
                  onNext={handleNext}
                  {...(stepProps[step] || {})}
                />
              </WithWrapper>
            </Column>
          </StepContainer>
          <ButtonContainer withSkipButton={!isFirst}>
            {skipData.skipButton && (
              <Button variant="muted" shape="square" onClick={handleSkipClick}>
                {skipData.skipButton}
              </Button>
            )}
            {!hideLastStepButton && !isFirst && (
              <Button
                icon={() => <ChevronLeftIcon size={24} />}
                disabled={nextLoading}
                onClick={handleBack}
                shape="square"
                size="md"
                variant="muted"
              />
            )}
            {withButtons && !stepProps[step]?.hideButtons && (
              <ContinueButton
                disabled={nextDisabled}
                loading={nextLoading}
                onClick={onPrimaryClick}
                shape="square"
                suffixIcon={!nextDisabled ? ArrowRightIcon : undefined}
                variant="primary"
              >
                {buttonText || messages.next}
              </ContinueButton>
            )}
          </ButtonContainer>
        </StepViewport>
        <ShowWhen when={!isMobile && !!ImageSrc}>
          <FunnelTestimonialContainer>
            {ImageSrc ? (
              <ImageSrc />
            ) : (
              <StepperTestimonial stepKey="default" imageSrc="mi-headshot" />
            )}
          </FunnelTestimonialContainer>
        </ShowWhen>
        {isOpen && (
          <ModalBase
            closable
            emoji="📑"
            header={messages.skipForNow}
            isOpen={isOpen}
            onClose={close}
            onPrimaryClick={() => {
              close()
              handleSkip()
            }}
            primaryText={messages.confirmSkip}
            secondaryText={messages.rejectSkip}
            subheader={<SkipModalSubheader>{skipData.text}</SkipModalSubheader>}
          />
        )}
      </StepperContainer>
    </Container>
  )
}

export const Stepper = (props: StepperComponentProps) => (
  <StepperProvider>
    <Component {...props} />
  </StepperProvider>
)
